/**
 * View or save an auth-gated PDF. The bytes are fetched through the
 * authenticated client as a Blob and handed to the browser as an object URL;
 * the endpoint URL is never opened directly (a new tab carries no JWT).
 */

/** Keep the object URL alive long enough for the new tab to load it. */
const REVOKE_AFTER_MS = 60_000;

/** `name` made filesystem-safe, with a `.pdf` extension. */
export function pdfFilename(name: string): string {
  return `${name.replace(/[^A-Za-z0-9._-]+/g, '-')}.pdf`;
}

/** Save a Blob via a temporary `<a download>`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking right after click() can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
}

/**
 * Open the PDF in a new tab, or save it as `filename` when `download` is set.
 * If the popup is blocked it is saved instead, so the user still gets it.
 * Rejects (after closing the blank tab) when the fetch fails.
 *
 * Must be called directly from a click handler: the blank tab opens before
 * any await, inside the click gesture, so popup blockers allow it.
 */
export async function openPdfBlob(
  fetchBlob: () => Promise<Blob>,
  filename: string,
  download = false,
): Promise<void> {
  const win = download ? null : window.open('', '_blank');
  if (win) {
    // `noopener` would make window.open return null, so drop the opener by hand.
    win.opener = null;
    win.document.title = 'Loading…';
  }
  try {
    const data = await fetchBlob();
    const blob = data.type === 'application/pdf' ? data : new Blob([data], { type: 'application/pdf' });
    // The user closed the tab while it loaded: treat that as a cancel.
    if (win?.closed) return;
    if (win) {
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
    } else {
      downloadBlob(blob, filename);
    }
  } catch (err) {
    win?.close();
    throw err;
  }
}
